/**
 * What the chat's approval card shows for a write the assistant proposed (CUR-2055).
 *
 * The server builds it from the pending write it stored for `confirmProcedureCall`, the same value
 * that runs on approval, so the card and the write cannot disagree. Every value is model or user
 * data: the client renders it as plain text, never as markdown or HTML.
 *
 * Browser-safe (no server imports): the server builds it and the client renders it.
 */
import { z } from 'zod';

/** Budgets for flattening a write's input into the card's field list. */
export const WRITE_APPROVAL_LIMITS = {
    /** Fields listed one by one. Past it, the rest of the input travels as JSON in `remainder`. */
    maxFields: 60,
    /** Path depth listed field by field. A deeper value is shown as JSON at its parent's path. */
    maxDepth: 6,
} as const;

/**
 * How a value was written, so the card can tell a text value from a literal or a structure:
 * `text` is a string as given, `literal` a number, boolean, null or a list of ids, and `json` a list
 * or object shown as JSON (an empty one, or one nested past `maxDepth`).
 */
export const WRITE_APPROVAL_VALUE_KINDS = ['text', 'literal', 'json'] as const;
export type WriteApprovalValueKind = (typeof WRITE_APPROVAL_VALUE_KINDS)[number];

export const writeApprovalFieldSchema = z
    .object({
        /** Where the value sits in the input: dotted keys, `[i]` for a list item, e.g. `set.obligations[0].obligationId`. */
        path: z.string(),
        kind: z.enum(WRITE_APPROVAL_VALUE_KINDS),
        /** The value to be written, in full. The card collapses a long one; it is never cut here. */
        value: z.string(),
    })
    .strict();
export type WriteApprovalField = z.infer<typeof writeApprovalFieldSchema>;

/** The record the write acts on, as an id: names come later with CUR-1630. */
export const writeApprovalTargetSchema = z
    .object({
        /** Sentence-case record type, e.g. `Complaint`. */
        label: z.string(),
        id: z.string(),
    })
    .strict();
export type WriteApprovalTarget = z.infer<typeof writeApprovalTargetSchema>;

export const writeApprovalDetailsSchema = z
    .object({
        target: writeApprovalTargetSchema.nullable(),
        fields: z.array(writeApprovalFieldSchema).max(WRITE_APPROVAL_LIMITS.maxFields),
        /**
         * The input past the field budget, never dropped: how many fields it holds, and those fields
         * as JSON keyed by path. Null when every field is listed.
         */
        remainder: z
            .object({
                fieldCount: z.number().int().positive(),
                json: z.string(),
            })
            .strict()
            .nullable(),
    })
    .strict();
export type WriteApprovalDetails = z.infer<typeof writeApprovalDetailsSchema>;
